import { useNavigate } from 'react-router-dom'

import { Box, GridBoxListItem as Item, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { Option } from 'modules/core/components/Popover/Content'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import countryList from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import employees from 'modules/employees/store/collections/employees'
import { TLocationsLocation } from 'modules/locations/hooks/useLocations'
import { useUpdateLocation } from 'modules/locations/hooks/useUpdateLocation'
import { showChar } from 'modules/locations/lib/helpers'
import { locationActiveEmployees } from 'modules/locations/lib/locationEmployees'
import locations from 'modules/locations/store/collections/locations'

interface Props {
  location: TLocationsLocation
  extra?: Record<string, string | number | boolean>
}

const LocationComponent = ({ location, extra }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { updateLocation } = useUpdateLocation()
  const list = countryList[i18n.locale as keyof typeof countryList] || countryList.en

  const country = list.find((item) => item.value === location.country)

  const locationStore = useMemo(() => {
    return locations.models.find((item) => item.id === location?.id)
  }, [location?.id])

  const employeesCount = locationStore
    ? locationActiveEmployees(locationStore, employees).length
    : 0

  const setHeadquarters = async () => {
    const result = await updateLocation({ id: location.id, main: true })
    const handled = handleResult(result, (errors) => {
      return errors
    })

    if (handled.success) {
      const oldLocation = locations.models.find(
        (l) => l.isMainLocation && location.id !== l.get('id')
      )

      if (oldLocation) {
        await updateLocation({ id: oldLocation.id, main: false })
      }
    }
  }

  const removeLocation = () => {
    navigate(navigation.settings.locations.removeId.fullPath({ id: location.id }))
  }

  const goToLocation = () => {
    track(trackingEvents.location.seeWorkplace, {})
    navigate(navigation.settings.locations.locationsId.details.fullPath({ id: location.id }))
  }

  const showOptions = (): Option[] => {
    const options: Option[] = []

    if (!location.main) {
      options.push({
        onClick: setHeadquarters,
        title: i18n.t('settings.locations.set_headquarters'),
      })
    }

    if (locations.models.length > 1) {
      options.push({
        onClick: removeLocation,
        title: i18n.t('settings.locations.remove_location'),
        type: 'wrong',
      })
    }

    return options
  }

  return (
    <Item
      title={showChar(location.name)}
      illustrationName="location-pin"
      options={showOptions()}
      icon="ellipsis"
      actionText={i18n.t('settings.locations.edit_location')}
      labelText={location.main ? 'HQ' : null}
      labelTooltipContent={location.main ? i18n.t('settings.locations.headquarters') : null}
      onClickAction={goToLocation}
      bang={!location.timezone}
      extra={extra}
    >
      <Box flexDirection="column" paddingTop="s8" gap="s8">
        {Boolean(country) && <Text.Normal>{country?.label}</Text.Normal>}
        <Text.Normal>
          {i18n.tp('settings.locations.employees_count', {
            count: employeesCount,
          })}
        </Text.Normal>
      </Box>
    </Item>
  )
}

export default observer(LocationComponent)
